import { Alert, Button, CheckboxGroup, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useState } from 'react'
import { SupplierFormData, supplierDetailsSchema } from '../SupplierForm.schema'
import z from 'zod'

type StateSchema = z.infer<typeof supplierDetailsSchema>

type Props = {
  data: SupplierFormData
  onNextClick: (data: StateSchema) => void
  onBackClick: () => void
}

export default function SupplierDetailsStep({ data, onNextClick, onBackClick }: Props) {
  const { t } = useI18n()

  const [state, setState] = useState<StateSchema>({
    supplierName: data.supplierName,
    businessRegistrationNumber: data.businessRegistrationNumber,
    isBusinessRegistrationNumberNotKnown: data.isBusinessRegistrationNumberNotKnown
  })

  const { supplierName, businessRegistrationNumber, isBusinessRegistrationNumberNotKnown } = state

  const handleSupplierNameChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setState({ ...state, supplierName: evt.target.value })
    },
    [state]
  )

  const handleBusinessRegistrationNumberChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setState({ ...state, businessRegistrationNumber: evt.target.value })
    },
    [state]
  )

  const handleIsBusinessRegistrationNumberNotKnownChange = useCallback(
    (val: boolean) => {
      setState({
        ...state,
        isBusinessRegistrationNumberNotKnown: val,
        businessRegistrationNumber: ''
      })
    },
    [state]
  )


  const validation = supplierDetailsSchema.safeParse(state)


  const handleNext = () => {
    return onNextClick(state)
  }

  return (
    <div className="space-y-6 pb-2">
      <h2 className="text-xl font-semibold mb-2">
        {t('suppliers:supplierInputFields.sections.suppliersDetails')}
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        {t('suppliers:createSupplierDialog.steps.descriptions.supplierDetails')}
      </p>
      <Alert type="info" className="mb-6">
        {t('suppliers:createSupplierDialog.steps.descriptions.supplierDetailsInfo')}
      </Alert>
      <div className="flex-1">
        <InputGroup
          label={t('suppliers:supplierInputFields.supplierName')}
          required
          value={supplierName || ''}
          onChange={handleSupplierNameChange}
        />
      </div>
      <div className="flex-1 space-y-2">
        <InputGroup
          required
          tooltip={t('suppliers:supplierInputFields.businessRegistrationNumber.tooltip')}
          label={t('suppliers:supplierInputFields.businessRegistrationNumber.label')}
          value={businessRegistrationNumber || ''}
          onChange={handleBusinessRegistrationNumberChange}
          disabled={isBusinessRegistrationNumberNotKnown}
        />
        <CheckboxGroup
          id="isBusinessRegistrationNumberNotKnown"
          checked={isBusinessRegistrationNumberNotKnown}
          onChange={handleIsBusinessRegistrationNumberNotKnownChange}
          label={t('suppliers:supplierInputFields.isBusinessRegistrationNumberNotKnown')}
        />
      </div>
      <div className="flex justify-between pt-16">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.cancel')}
        </Button>
        <Button onClick={handleNext} disabled={!validation.success}>
          {t('common:actions.next')}
        </Button>
      </div>
    </div>
  )
}
